/**
 * LiveSignupCounter — social-proof widget for the pricing page.
 *
 * Prefers the SSE stream at `/api/growth/signup-stream` so the number
 * updates in near-real-time (5-second heartbeat). Falls back to polling
 * `/api/growth/signup-count?days=7` if the browser can't open the stream.
 *
 * Bumps the pulse animation when a real delta arrives — a visible
 * "someone just joined" signal without any WebSocket infra.
 */
import { useEffect, useState, useRef } from "react";
import { Users, Sparkles } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function LiveSignupCounter() {
  const [count, setCount] = useState(null);
  const [pulse, setPulse] = useState(false);
  const [justBumped, setJustBumped] = useState(false);
  const esRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const applyCount = (n, delta = 0) => {
      if (cancelled) return;
      if (!Number.isFinite(n) || n < 0) { setCount(null); return; }
      setCount(n);
      setPulse(true);
      setTimeout(() => setPulse(false), 900);
      // Fire the "just bumped" animation only when a real signup delta
      // is reported (not on the initial hydrate) so users see something
      // visibly change and feel the urgency of the moment.
      if (delta > 0) {
        setJustBumped(true);
        setTimeout(() => setJustBumped(false), 3_500);
      }
    };

    // Try the SSE stream first — 5-sec heartbeat with real signup deltas.
    try {
      const es = new EventSource(`${API}/api/growth/signup-stream`);
      esRef.current = es;
      es.addEventListener("signup", (e) => {
        try {
          const j = JSON.parse(e.data);
          applyCount(Number(j?.count), Number(j?.delta || 0));
        } catch { /* ignore malformed frames */ }
      });
      es.onerror = () => {
        // Fall back to poll if the stream drops.
        try { es.close(); } catch {}
        esRef.current = null;
        pollOnce();
      };
    } catch {
      pollOnce();
    }

    // Fallback poll (also used as the initial hydrate if SSE is blocked).
    const pollOnce = () => {
      fetch(`${API}/api/growth/signup-count?days=7`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => applyCount(typeof d?.count === "number" ? d.count : NaN))
        .catch(() => setCount(null));
    };

    return () => {
      cancelled = true;
      try { esRef.current?.close(); } catch {}
    };
  }, []);

  if (count === null || count === 0) return null;

  return (
    <div
      data-testid="live-signup-counter"
      className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold transition-transform ${pulse ? "animate-pulse" : ""} ${justBumped ? "scale-110" : ""}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping"/>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"/>
      </span>
      <Users className="w-3.5 h-3.5"/>
      <span className="tabular-nums font-extrabold">{count.toLocaleString()}</span>
      <span>Canadian landlords signed up this week</span>
      <Sparkles className="w-3.5 h-3.5 text-emerald-600"/>
      {justBumped && (
        <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">+ new!</span>
      )}
    </div>
  );
}
